import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Classifications } from '../../../account-info-header/models';
import {
  selectClassifications,
  selectMasterAccount,
  selectSubAccountId,
} from '../../../account-info-header/reducers';
import {
  InternalTransferActions,
  InternalTransfersActions,
  PtrsActions,
} from '../../../create-request/actions';
import { OnetimeInputRequest } from '../../../create-request/models/internal-transfer/InternalTransferInputRequest';
import { ToAccountInfo } from '../../../create-request/models/internal-transfer/ToAccountInfo';
import { createControl } from '../../../create-request/utilities/formUtilities';
import { StandingAuthInternalTransferActions } from '../../../standing-authorizations/actions';
import { RemoveHyphenFromAccountPipe } from '../../pipes/remove-hyphen-from-account.pipe';
import { ValidateInputService } from '../../services/validate-input.service';
import { ComponentState } from '../../types/ComponentState';
import { Constants, receivingAccountErrorMap } from '../../utilities/Constants';
import { toAccountAllowedChars } from '../../utilities/regex-patterns';
import { InfoToAccount } from '../../models/InfoToAccount';
import { selectIsCreateStandingAuth, selectIsCreateTransaction } from '../../reducers';
import { selectIsFromEdit } from '../../../create-request/reducers';

@Component({
  standalone: false,
  selector: 'mm-it-to-account-number',
  templateUrl: './it-to-account-number.component.html',
  styleUrls: ['./it-to-account-number.component.scss'],
})
export class ToAccountNumberComponent implements OnInit, OnDestroy {
  @Input() parentForm: FormGroup;
  @Input() labelName: string = 'Account to receive funds';
  @Input() isRecurringTransaction: boolean = false;
  @Input() disabled: boolean | null;
  @Input() info$: Observable<InfoToAccount>;
  @Input() toAccountState$: Observable<ComponentState>;
  @Output() resetForm = new EventEmitter<void>();
  toAccountAllowedChars = toAccountAllowedChars;
  hyphenPipe = new RemoveHyphenFromAccountPipe();
  destroy$: Subject<boolean> = new Subject<boolean>();
  toAccountInContext: string;
  subAccountId: string;
  masterAccount: string;
  accountClassifications: Classifications;
  isToAccountInContext: boolean;
  toAccountInfo: ToAccountInfo;
  showAccountRegDetails: boolean;
  readonly controlNames = {
    toAccount: 'toAccount',
  };
  isCreateTransactionPage: boolean;
  isCreateStandingAuthPage: boolean;
  isEdit: boolean;

  constructor(
    private store: Store,
    public validationService: ValidateInputService
  ) { }

  ngOnInit(): void {
    createControl(
      this.parentForm,
      this.controlNames.toAccount,
      '',
      [
        Validators.required,
        Validators.minLength(Constants.toAccountInputLength),
        Validators.maxLength(Constants.toAccountMaxInputLength),
      ],
      undefined,
      this.disabled ?? false,
      false
    );

    this.store
      .select(selectSubAccountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sub) => {
        this.subAccountId = sub;
      });

    this.store
      .select(selectMasterAccount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((masterAccount) => {
        this.masterAccount = masterAccount;
      });

    this.store
      .select(selectClassifications)
      .pipe(takeUntil(this.destroy$))
      .subscribe((classifications) => {
        this.accountClassifications = classifications;
      });

    this.info$.pipe(takeUntil(this.destroy$)).subscribe((info) => {
      if (info.inputCallFinished) {
        this.isToAccountInContext = true;
        if (!this.toAccountInContext) {
          this.toAccountInContext = this.toAccount?.value || info.toAccountInfo.accountNumber;
        }
      }
      this.toAccountInfo = info.toAccountInfo;
      this.showAccountRegDetails = info.inputCallFinished || this.toAccountInfo.accountRegistration.length > 0;
    });
    
    this.store.select(selectIsCreateTransaction).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateTransactionPage = val);
    this.store.select(selectIsCreateStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateStandingAuthPage = val);
  }

  ngOnChanges(): void {
    // Handle disabled state changes for the toAccount form control
    if (this.disabled) {
      this.toAccount?.disable();
    } else {
      this.toAccount?.enable();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get toAccount(): FormControl {
    return this.parentForm.get('toAccount') as FormControl;
  }

  get toAccountFormIsInvalid() {
    return this.toAccount.touched && this.toAccount.invalid;
  }

  onSubmit(): void {
    if (
      this.toAccount.valid &&
      this.toAccount.value !== this.toAccountInContext
    ) {
      let payload: OnetimeInputRequest = {
        IRAAccount: false,
        masterAccountId: this.masterAccount,
        subAccountId: this.subAccountId,
        bsloaTransaction: false,
        toAccountId: this.hyphenPipe.transform(this.toAccount.value),
        isRecurringTransaction: this.isCreateStandingAuthPage
          ? false
          : this.isRecurringTransaction,
        isCreateSloa: false,
        newRegTypeEnabled: true,
      };
      this.toAccountInContext = this.toAccount.value;
      this.callInput(payload);
    }
  }

  callInput(payload: OnetimeInputRequest): void {
    if (this.isCreateTransactionPage) {
      this.store.dispatch(
        InternalTransferActions.toAccountEntered({ payload })
      );
      if (this.accountClassifications.isPtrs) {
        this.store.dispatch(
          PtrsActions.callPtrs({ accountId: this.subAccountId })
        );
      }
    } else if (this.isCreateStandingAuthPage) {
      this.store.dispatch(
        StandingAuthInternalTransferActions.toAccountEntered({ payload })
      );
    }
  }

  onInput(): void {
    let toAccountValue = this.toAccount?.value;
    if (this.isToAccountInContext) {
      this.clearToAccountClicked();
    }

    if (!toAccountValue) return;
    toAccountValue = toAccountValue.replace(/[^\d]/g, '');
    const group = toAccountValue.match(/^(.{4})(.{1,5})(.*)/);

    if (group) {
      toAccountValue = `${group[1]}-${group[2]}`;
    }

    if (toAccountValue.length > 10) {
      toAccountValue = toAccountValue.substring(0, 9);
    }
    this.toAccount?.setValue(toAccountValue);
  }

  clearToAccountClicked(): void {
    this.resetForm.emit();
    this.isToAccountInContext = false;
    this.store.dispatch(InternalTransfersActions.itClearToAccount({ account: this.toAccountInContext }));
    this.toAccountInContext = '';
    this.toAccount.reset();
    if (this.isCreateStandingAuthPage) {
      this.store.dispatch(StandingAuthInternalTransferActions.clearToAccount());
    }
  }

  get toAccountValidationError() {
    if (this.toAccount && this.toAccount.errors) {
      const errorKeys: string[] = Object.keys(this.toAccount.errors ?? {});
      if (errorKeys.length > 0) {
        return receivingAccountErrorMap[
          errorKeys[0] as keyof typeof receivingAccountErrorMap
        ];
      }
    }
  }

  get receivingAccountDetails(): string[] {
    if (this.hasAccountRegDetails) {
      return this.toAccountInfo.accountRegistration;
    }
    return [Constants.toAccountInfoUnavailable];
  }

  get hasAccountRegDetails(): boolean {
    return this.toAccountInfo?.accountRegistration?.length > 0;
  }
}
